import React, { useEffect, useState, useMemo, useRef } from "react";
import { Flex, Text, TextField, Button, Dialog } from "@radix-ui/themes";
import { ActiveIntegrateCard } from "components";
import { PermissionAlertDialog } from "components";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";

// Hooks expected to exist in your codebase
import {
  useGetMetaConfigs,
  useGetAllBrands,
  useActivateMeta,   // <- use this to save (backend uses update_or_create)
  useDeactivateMeta,
  useGetOrgLocationsByUser
} from "hooks";

export function ActiveMetaIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems } = useLocationContext();
  const { setToastMessage } = useToast();

  const [localConfigs, setLocalConfigs] = useState([]); // rows: { id, type, name, pixelId, accessTokenMasked, accessToken }
  const [originalConfigs, setOriginalConfigs] = useState([]);
  const [showTokenMap, setShowTokenMap] = useState({}); // key -> bool (true => show plain text)
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");

  // Check if user is owner
  const isOwner = userProfile.user_role === "Owner";

  // only initialize rows once (or when the incoming data actually changes)
  const initializedRef = useRef(false);

  const { data: metaConfigsData, isLoading: isConfigsLoading } = useGetMetaConfigs(userProfile?.organisation_id);
  // const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  // const { data: locationsData } = useGetOrgLocationsByUser(userProfile?.user_id);
  const activateMeta = useActivateMeta();
  const deactivateMeta = useDeactivateMeta();

  const { locationsData } = useLocationContext();
  // Fetch all brands for this organisation using the same hook as OrganisationBrands
  const { data: brandsData, isLoading: isLoadingBrands, error: brandsError } = useGetAllBrands(userProfile.organisation_id);

  // State to hold filtered data
  const [brands, setBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);

      // Filter out the default brand and get non-default brands
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);
      setBrands(nonDefaultBrands);

      // Filter locations that use the default brand
      const defaultLocations = locationsData.filter((location) => location.brand_id === defaultBrandItem?.id || location.brand_id === null);
      setIndependentLocations(defaultLocations);
    }
  }, [brandsData, locationsData]);

  useEffect(() => {
    if (!metaConfigsData?.data || !brands || !independentLocations) return;

    const saved = metaConfigsData.data.configs;
    // const brands = brandsData || [];
    // const locations = locationsData || [];

    const rows = [];

    brands.forEach(b => {
      const cfg = saved.find(c => c.type === "brand" && String(c.id) === String(b.id));
      rows.push({
        id: b.id,
        type: "brand",
        name: b.name,
        pixelId: cfg?.pixel_id || "",
        accessTokenMasked: cfg?.has_token ? "••••••" : "",
        accessToken: cfg?.access_token || "",
        textfield_type: "password"
      });
    });

    independentLocations.forEach(loc => {
      const cfg = saved.find(c => c.type === "location" && String(c.id) === String(loc.id || loc.value));
      rows.push({
        id: loc.id,
        type: "location",
        name: loc.name,
        pixelId: cfg?.pixel_id || "",
        accessTokenMasked: cfg?.has_token ? "••••••" : "",
        accessToken: cfg?.access_token || "",
        textfield_type: "password"
      });
    });

    setLocalConfigs(rows);
    setOriginalConfigs(rows.map(r => ({ ...r })));
    setErrors({});
  }, [metaConfigsData, brands, locationsData]);

  // utility key
  const keyFor = (r) => `${r.type}-${r.id}`;

  // const clearErrorField = (rowKey, field) => {
  //   setErrors(prev => {
  //     if (prev[rowKey]) {
  //       delete prev[rowKey][field];
  //     }
  //     return { ...prev };
  //   });
  // };
  const clearErrorField = (rowKey, field, row) => {
    setErrors(prev => {
      if (!prev[rowKey]) return prev;

      // Only clear the error if both pixelId and accessToken are either filled or both empty
      const isValid = Boolean(row.pixelId?.trim()) === Boolean(row.accessToken?.trim());

      if (isValid) {
        const newRowErr = { ...prev[rowKey] };
        delete newRowErr[field];

        const copy = { ...prev };
        if (Object.keys(newRowErr).length === 0) {
          delete copy[rowKey];
        } else {
          copy[rowKey] = newRowErr;
        }

        return copy;
      }

      return prev; // leave errors if row is still invalid
    });
  };


  const handlePixelChange = (idx, value) => {
    setLocalConfigs(prev => {
      const next = [...prev];
      const rowKey = keyFor(next[idx]);
      next[idx] = { ...next[idx], pixelId: value };
      clearErrorField(rowKey, "pixelId", next[idx]);
      return next;
    });
  };

  const handleAccessTokenFocusBlurChange = (event, idx) => {
    const type =
      event.type === "focus" ? "text" :
        event.type === "blur" ? "password" : null;

    const value = event.target.value;

    setLocalConfigs(prev => {
      const next = [...prev];
      const rowKey = keyFor(next[idx]);
      next[idx] = {
        ...next[idx],
        accessToken: value,
        textfield_type: type || next[idx].textfield_type
      };
      clearErrorField(rowKey, "accessToken", next[idx]);
      return next;
    });
  };


  const handleChanges = (event, rowKey, field) => {
    const value = event?.target?.value ?? "";

    setLocalConfigs(prev =>
      prev.map(item =>
        keyFor(item) === rowKey ? { ...item, [field]: value } : item
      )
    );
  };

  const changedRows = useMemo(() => {
    return localConfigs.filter((r) => {
      const orig = originalConfigs.find(o => String(o.id) === String(r.id) && o.type === r.type) || {};
      const pixelChanged = String((r.pixelId || "").trim()) !== String((orig.pixelId || "").trim());
      const tokenChanged = String(r.accessToken || "") !== String(orig.accessToken || "");
      return pixelChanged || tokenChanged;
    });
  }, [localConfigs, originalConfigs]);

  const validateRow = (row) => {
    const e = {};
    const hasPixel = String(row.pixelId || "").trim() !== "";
    const hasToken = String(row.accessToken || "").trim() !== "";

    if (hasToken && !hasPixel) {
      // Access token provided but no pixel -> mark pixel as error
      e.pixelId = true;
    }

    if (!hasToken && hasPixel) {
      // Pixel provided but no access token -> mark token as error
      e.accessToken = true;
    }

    return e;
  };

  const handleSave = async () => {
    if (!activateMeta) {
      setToastMessage("An error occured.");
      return;
    }
    if (changedRows.length === 0) {
      setToastMessage("No changes to save");
      return;
    }

    const validationErrors = {};
    changedRows.forEach(r => {
      const err = validateRow(r);
      if (Object.keys(err).length) {
        validationErrors[keyFor(r)] = err;
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setFormErrorMessage("Please fill in both Pixel ID and Access Token for rows marked in red.");
      return;
    }

    setFormErrorMessage(""); // clear message

    // const payloadConfigs = changedRows.map(r => ({
    //   id: r.id,
    //   type: r.type,
    //   pixel_id: r.pixelId?.trim(),
    //   ...(r.accessToken ? { access_token: r.accessToken } : {}),
    // }));
    const payloadConfigs = changedRows.map(r => ({
      id: r.id,
      type: r.type,
      pixel_id: (r.pixelId || "").trim(),
      access_token: (r.accessToken || "").trim(),
    }));

    try {
      setIsSaving(true);
      const result = await activateMeta.mutateAsync({
        organisation_id: userProfile.organisation_id,
        configs: payloadConfigs,
      });

      if (!(result && result.data && result.data.status === "success")) {
        throw new Error((result && result.data && result.data.message) || "Failed to save Meta configs");
      }

      queryClient.invalidateQueries(["meta-configs"]);
      queryClient.invalidateQueries(["meta-integration-status"]);
      setToastMessage("Meta settings saved");
    } catch (err) {
      console.error("Error saving Meta configs:", err);
      setToastMessage(err.message || "Failed to save Meta configs");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle disconnect click with permission check
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);

      const result = await deactivateMeta.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });

      if (result?.data?.status === "success") {
        // Invalidate the status query to refresh the active integrations list
        queryClient.invalidateQueries(["meta-integration-status"]);
        // setToastMessage("Airship integration disconnected successfully");
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Meta integration");
      }
    } catch (error) {
      // console.error("Error deactivating Meta:", error);
      setToastMessage(error.message || "Failed to disconnect Meta integration");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  if (isConfigsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: 200 }}>
        <Text>Loading Meta integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Meta"
        subtitle="Facebook / Meta Events (Pixels & Conversions API)"
        image_url="meta.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="Pixels & Tokens"
          subtitle="Assign a Pixel to each brand/location. If you need to (re)enter an access token, add it here."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            {/* Header row */}
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "32%" }}>Entity</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "3px" }}>Pixel ID</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "5px" }}>Access Token</Text>
            </Flex>

            {localConfigs.length > 0 ? (
              localConfigs.map((row, idx) => {
                const rowKey = keyFor(row);
                const rowErr = errors[rowKey] || {};

                return (
                  <Flex key={rowKey} direction="row" justify="between" style={{ width: "100%" }} align="center">
                    {/* Entity name */}
                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      <Flex
                        style={{
                          width: "100%",
                          backgroundColor: "var(--gray-3)",
                          border: "1px solid var(--gray-a7)",
                          borderRadius: "var(--radius-2)",
                          padding: "0 var(--space-2)",
                          height: "34px",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Text size="2" title={row.name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {row.name}
                        </Text>
                      </Flex>
                    </Flex>

                    {/* Pixel ID input */}
                    <Flex style={{ width: "32%", minWidth: 0, paddingLeft: 8, paddingRight: 8 }}>
                      <TextField.Root
                        className={rowErr.pixelId && "border border-red-500"}
                        autoComplete="off"
                        inputMode="numeric"
                        placeholder="Pixel ID"
                        value={row.pixelId}
                        onChange={(e) => {
                          const v = e.target.value;
                          handlePixelChange(idx, v);
                          handleChanges({ type: "change", target: { value: v } }, rowKey, "pixelId");
                        }}
                        disabled={!isOwner}
                        size="2"
                        variant="surface"
                        style={{
                          width: "100%",
                          height: "34px",
                          borderRadius: "var(--radius-2)",
                        }}
                      />
                    </Flex>

                    {/* Access token (password-style field) */}
                    <Flex style={{ width: "32%", minWidth: 0, paddingLeft: 8, paddingRight: 8 }}>
                      <TextField.Root
                        className={rowErr.accessToken && "border border-red-500"}
                        autoComplete="new-password"
                        placeholder="Access token"
                        type={row.textfield_type}
                        value={row.accessToken}
                        onFocus={(e) => handleAccessTokenFocusBlurChange(e, idx)}
                        onBlur={(e) => handleAccessTokenFocusBlurChange(e, idx)}
                        onChange={(e) => handleAccessTokenFocusBlurChange(e, idx)}
                        disabled={!isOwner}
                        size="2"
                        variant="surface"
                        style={{
                          width: "100%",
                          height: "34px",
                          borderRadius: "var(--radius-2)",
                          boxSizing: "border-box",
                        }}
                      />
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No entities found.</Text>
              </Flex>
            )}

            {formErrorMessage && (
              <Flex justify="start" style={{ marginTop: 8 }}>
                <Text size="2" color="red">{formErrorMessage}</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSave}
                loading={isSaving}
                disabled={!isOwner || isSaving || changedRows.length === 0}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Disconnect confirmation */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Meta</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Disconnecting will stop event / contact sync from this organisation to Meta.
          </Dialog.Description>

          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
